import { format } from 'date-fns';
import { durationFromSecs } from 'utils/helpers';
import { CTF_LAB_TAG } from 'constants/playlists';
import {
  CUSTOM_CONTENT_METRICS_KEYS,
  ICustomContentItem,
  ICustomContentMetric,
} from 'services/customContent';
import { ICustomContentCardItemView } from './types';

type ICustomContentTypeVisual = ICustomContentCardItemView['contentTypeVisual'];

const DEFAULT_CONTENT_TYPE_VISUAL: ICustomContentTypeVisual = {
  label: 'Content',
  icon: 'PlaylistIcon',
  backgroundColor: 'var(--color-info-light, #E5EBF5)',
  color: 'var(--color-info-strong, #314F78)',
};

const COURSE_VISUAL: ICustomContentTypeVisual = {
  label: 'Course',
  icon: 'ContentTypeCourse',
  backgroundColor: 'var(--lilac-lighter, #D3C9FA)',
  color: 'var(--color-accent-purple, #634EE6)',
};

const LAB_VISUAL: ICustomContentTypeVisual = {
  label: 'Lab',
  icon: 'ContentTypeLab',
  backgroundColor: 'var(--apricot-peach, #F8CEAD)',
  color: 'var(--color-accent-red-dark, #C11E05)',
};

const CTF_LAB_VISUAL: ICustomContentTypeVisual = {
  label: CTF_LAB_TAG,
  icon: 'ContentTypeLab',
  backgroundColor: 'var(--apricot-peach, #F8CEAD)',
  color: 'var(--color-accent-red-dark, #C11E05)',
};

const ASSESSMENT_VISUAL: ICustomContentTypeVisual = {
  label: 'Assessment',
  icon: 'ContentTypeAssessment',
  backgroundColor: 'var(--content-slide, #B2D4D1)',
  color: 'var(--color-accent-teal-dark, #2B655E)',
};

const LEARNING_PATH_VISUAL: ICustomContentTypeVisual = {
  label: 'Learning Path',
  icon: 'ContentTypeLearningPath',
  backgroundColor: 'var(--pink-lighter, #FEC3CB)',
  color: 'var(--color-danger-vivid, #F5121A)',
};

const CONTENT_TYPE_VISUALS: Record<string, ICustomContentTypeVisual> = {
  course: COURSE_VISUAL,
  lab: LAB_VISUAL,
  ctf_lab: CTF_LAB_VISUAL,
  assessment: ASSESSMENT_VISUAL,
  learning_path: LEARNING_PATH_VISUAL,
};

const CONTENT_TYPE_ALIASES: Record<string, keyof typeof CONTENT_TYPE_VISUALS> = {
  standard_lab: 'lab',
  learning_path_with_cert: 'learning_path',
  skill_assessment: 'assessment',
  skills_assessment: 'assessment',
  learningpath: 'learning_path',
};

const toReadableLabel = (value: string): string => {
  return value
    .replace(/[-_]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');
};

const PERCENTAGE_METRIC_KEYS: Set<string> = new Set([
  CUSTOM_CONTENT_METRICS_KEYS.COMPLETION_RATE,
  CUSTOM_CONTENT_METRICS_KEYS.AVG_PROGRESS,
  CUSTOM_CONTENT_METRICS_KEYS.EXAM_PARTICIPATION_RATE,
  CUSTOM_CONTENT_METRICS_KEYS.EXAM_PASS_RATE,
  CUSTOM_CONTENT_METRICS_KEYS.AVG_SCORE,
  CUSTOM_CONTENT_METRICS_KEYS.PASS_RATE,
  CUSTOM_CONTENT_METRICS_KEYS.IMPROVEMENT_RATE,
  CUSTOM_CONTENT_METRICS_KEYS.FULL_COMPLETION_RATE,
]);

const formatPercentageValue = (value: string): string => {
  const normalizedValue = value.trim();

  if (!normalizedValue) {
    return value;
  }

  if (normalizedValue.includes('%')) {
    return normalizedValue;
  }

  const numericValue = Number(normalizedValue);
  if (!Number.isFinite(numericValue)) {
    return value;
  }

  if (numericValue === 0) {
    return '0%';
  }

  const percentageValue = numericValue > 0 && numericValue <= 1 ? numericValue * 100 : numericValue;
  return `${Math.round(percentageValue)}%`;
};

export const formatCustomContentMetricValue = (metric: ICustomContentMetric): string => {
  if (!PERCENTAGE_METRIC_KEYS.has(metric.key)) {
    return metric.value;
  }

  return formatPercentageValue(metric.value);
};

export const CUSTOM_CONTENT_METRIC_TOOLTIP_TEXT = {
  engagedUsersPath: 'Number of unique users who started at least one item in the path',
  engagedUsersCourse: 'Number of unique users who started at least one item in the course',
  engagedUsersLab: 'Number of unique users who started the lab',
  completionRatePath: '% of engaged users who completed the path',
  completionRateCourse: '% of engaged users who completed the course',
  completionRateLab: '% of users who completed the lab',
  averageProgressPath: 'Average % of the path completed across all engaged users',
  averageProgressCourse: 'Average % of the course completed across all engaged users',
  examParticipationRate: '% of engaged users who attempted the certification exam',
  examPassRate: '% of exam takers who successfully passed the certification exam',
  assessmentAttempts: 'Number of users who took the assessment at least once',
  averageScore: 'Mean score across all assessment attempts',
  passRate: '% of users who achieved or exceeded the passing threshold',
  improvementRate: '% of users whose score improved on a subsequent attempt',
  totalStarts: 'Total number of times this lab was started (includes repeat users)',
  averageSessionDuration: 'Average time spent actively engaged in a lab environment',
  averageFlagsCaptured: 'Average number of flags captured per user',
  fullCompletionRate: '% of users who captured all available flags',
} as const;

export const CUSTOM_CONTENT_METRIC_TOOLTIPS = {
  [CUSTOM_CONTENT_METRICS_KEYS.EXAM_PARTICIPATION_RATE]:
    CUSTOM_CONTENT_METRIC_TOOLTIP_TEXT.examParticipationRate,
  [CUSTOM_CONTENT_METRICS_KEYS.EXAM_PASS_RATE]: CUSTOM_CONTENT_METRIC_TOOLTIP_TEXT.examPassRate,
  [CUSTOM_CONTENT_METRICS_KEYS.ATTEMPTS]: CUSTOM_CONTENT_METRIC_TOOLTIP_TEXT.assessmentAttempts,
  [CUSTOM_CONTENT_METRICS_KEYS.AVG_SCORE]: CUSTOM_CONTENT_METRIC_TOOLTIP_TEXT.averageScore,
  [CUSTOM_CONTENT_METRICS_KEYS.PASS_RATE]: CUSTOM_CONTENT_METRIC_TOOLTIP_TEXT.passRate,
  [CUSTOM_CONTENT_METRICS_KEYS.IMPROVEMENT_RATE]:
    CUSTOM_CONTENT_METRIC_TOOLTIP_TEXT.improvementRate,
  [CUSTOM_CONTENT_METRICS_KEYS.TOTAL_STARTS]: CUSTOM_CONTENT_METRIC_TOOLTIP_TEXT.totalStarts,
  [CUSTOM_CONTENT_METRICS_KEYS.AVG_SESSION_DURATION]:
    CUSTOM_CONTENT_METRIC_TOOLTIP_TEXT.averageSessionDuration,
  [CUSTOM_CONTENT_METRICS_KEYS.AVG_FLAGS_CAPTURED]:
    CUSTOM_CONTENT_METRIC_TOOLTIP_TEXT.averageFlagsCaptured,
  [CUSTOM_CONTENT_METRICS_KEYS.FULL_COMPLETION_RATE]:
    CUSTOM_CONTENT_METRIC_TOOLTIP_TEXT.fullCompletionRate,
} as const;

const toNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

export const normalizeCustomContentType = (value: unknown): string => {
  const contentType = toNonEmptyString(value)?.toLowerCase();
  if (!contentType) return '';
  return contentType.replace(/-/g, '_');
};

const LAB_CONTENT_TYPES = ['standard_lab', 'ctf_lab', 'lab'] as const;

type LabContentType = typeof LAB_CONTENT_TYPES[number];

const isLabContentType = (contentType?: string): contentType is LabContentType =>
  Boolean(contentType && LAB_CONTENT_TYPES.includes(contentType as LabContentType));

export const getMetricTooltip = (metricKey: string, contentType?: string): string | undefined => {
  const normalizedContentType = normalizeCustomContentType(contentType);

  if (metricKey === CUSTOM_CONTENT_METRICS_KEYS.ENGAGED_USERS) {
    if (normalizedContentType === 'course') {
      return CUSTOM_CONTENT_METRIC_TOOLTIP_TEXT.engagedUsersCourse;
    }

    if (isLabContentType(normalizedContentType)) {
      return CUSTOM_CONTENT_METRIC_TOOLTIP_TEXT.engagedUsersLab;
    }

    return CUSTOM_CONTENT_METRIC_TOOLTIP_TEXT.engagedUsersPath;
  }

  if (metricKey === CUSTOM_CONTENT_METRICS_KEYS.COMPLETION_RATE) {
    if (normalizedContentType === 'course') {
      return CUSTOM_CONTENT_METRIC_TOOLTIP_TEXT.completionRateCourse;
    }

    if (isLabContentType(normalizedContentType)) {
      return CUSTOM_CONTENT_METRIC_TOOLTIP_TEXT.completionRateLab;
    }

    return CUSTOM_CONTENT_METRIC_TOOLTIP_TEXT.completionRatePath;
  }

  if (metricKey === CUSTOM_CONTENT_METRICS_KEYS.AVG_PROGRESS) {
    return normalizedContentType === 'course'
      ? CUSTOM_CONTENT_METRIC_TOOLTIP_TEXT.averageProgressCourse
      : CUSTOM_CONTENT_METRIC_TOOLTIP_TEXT.averageProgressPath;
  }

  return CUSTOM_CONTENT_METRIC_TOOLTIPS[metricKey as keyof typeof CUSTOM_CONTENT_METRIC_TOOLTIPS];
};

const toVisibility = (value: unknown): ICustomContentCardItemView['visibility'] => {
  return value === 'limited' ? 'limited' : 'unlimited';
};

const toRecord = (value: unknown): Record<string, unknown> => {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return {};
};

const hashString = (value: string): string => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }

  return Math.abs(hash).toString(36);
};

const buildFallbackId = (
  safeItem: Record<string, unknown>,
  contentData: Record<string, unknown>,
  fallbackSeed?: number
): string => {
  const source =
    [
      toNonEmptyString(safeItem.content_type),
      toNonEmptyString(contentData.name),
      toNonEmptyString(safeItem.created),
      toNonEmptyString(safeItem.modified),
      toNonEmptyString(safeItem.business_account_id),
    ]
      .filter(Boolean)
      .join('|') || 'custom-content';

  const baseId = `custom-content-${hashString(source)}`;
  if (typeof fallbackSeed === 'number') {
    return `${baseId}-${fallbackSeed}`;
  }

  return baseId;
};

const getContentTypeVisual = (rawContentType: unknown): ICustomContentTypeVisual => {
  const normalizedContentType = normalizeCustomContentType(rawContentType);
  if (!normalizedContentType) {
    return DEFAULT_CONTENT_TYPE_VISUAL;
  }

  const canonicalContentType = CONTENT_TYPE_ALIASES[normalizedContentType] ?? normalizedContentType;
  const mappedVisual = CONTENT_TYPE_VISUALS[canonicalContentType];
  if (mappedVisual) {
    return mappedVisual;
  }

  return {
    ...DEFAULT_CONTENT_TYPE_VISUAL,
    label: toReadableLabel(normalizedContentType),
  };
};

const toDifficulty = (value: unknown): string => {
  const asText = toNonEmptyString(value);
  if (!asText) return 'N/A';

  return asText.charAt(0).toUpperCase() + asText.slice(1).toLowerCase();
};

const toDuration = (value: unknown): string => {
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds > 0) {
    const parsed = durationFromSecs(seconds, 'minutes').trim();
    if (parsed) {
      return parsed;
    }

    const fallbackMinutes = Math.max(1, Math.round(seconds / 60));
    return `${fallbackMinutes}m`;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || 'N/A';
  }

  return 'N/A';
};

const toAddedDateLabel = (createdAt: unknown, publishedAt: unknown): string => {
  const selectedDate = toNonEmptyString(createdAt) || toNonEmptyString(publishedAt);
  if (!selectedDate) {
    return 'Added: -';
  }

  const parsedDate = new Date(selectedDate);
  if (Number.isNaN(parsedDate.getTime())) {
    return 'Added: -';
  }

  return `Added: ${format(parsedDate, 'MMMM d, yyyy')}`;
};

export const normalizeCustomContentItem = (
  item: ICustomContentItem,
  fallbackSeed?: number
): ICustomContentCardItemView => {
  const safeItem = toRecord(item);
  const contentData = toRecord(safeItem.content_data);
  const difficultyValue = contentData.difficulty ?? safeItem.difficulty;
  const durationValue =
    contentData.duration_in_seconds ??
    contentData.duration ??
    safeItem.duration_in_seconds ??
    safeItem.duration;

  const title =
    toNonEmptyString(contentData.name) ||
    toNonEmptyString(safeItem.content_id) ||
    'Untitled content';
  const description =
    toNonEmptyString(contentData.description) || 'No description available for this content yet.';
  const difficulty = toDifficulty(difficultyValue);
  const duration = toDuration(durationValue);

  return {
    id:
      toNonEmptyString(safeItem.id) ||
      toNonEmptyString(safeItem.content_id) ||
      buildFallbackId(safeItem, contentData, fallbackSeed),
    title,
    description,
    difficulty,
    duration,
    added: toAddedDateLabel(safeItem.created, contentData.published_date),
    contentTypeVisual: getContentTypeVisual(safeItem.content_type),
    visibility: toVisibility(safeItem.visibility),
    rawItem: item,
  };
};

export const normalizeCustomContentItems = (
  items: ICustomContentItem[]
): ICustomContentCardItemView[] => {
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => normalizeCustomContentItem(item, index));
};
